import { ContainedIcon } from '@/components/ContainedIcon/ContainedIcon.tsx'
import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { RouteMap } from '@/components/RouteMap/RouteMap.tsx'
import type { Route as DrivingRoute } from '@/features/routing/api/route-list.service'
import { PropertyBlock } from '@/features/routing/components/layout/assignment/PropertyBlock.tsx'
import { DriverInfoSection } from '@/features/routing/components/ui/map/DriverInfo.tsx'
import { MetricItem } from '@/features/routing/components/ui/map/MetricItem'
import { VehicleInfoSection } from '@/features/routing/components/ui/map/VehicleInfoSection.tsx'
import useRouteList from '@/features/routing/hooks/useRouteList.tsx'
import type { User } from '@/features/user/api'
import { useGetAllUsersByRole } from '@/features/user/hooks'
import type { Vehicle } from '@/features/vehicles/api'
import { useAllVehiclesForCompany } from '@/features/vehicles/hooks'
import i18n from '@/i18n'
import { blueTechVariants } from '@/themes/colors.ts'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import AirlineStopsOutlinedIcon from '@mui/icons-material/AirlineStopsOutlined'
import InboxIcon from '@mui/icons-material/Inbox'
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined'
import RouteIcon from '@mui/icons-material/Route'
import ShuffleIcon from '@mui/icons-material/Shuffle'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import { Box, Button, Card, Divider, Grid, Stack, Typography } from '@mui/material'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { startOfDay } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FEATURES, isFeatureEnabled } from '@/utils/feature-flag'
import { PageSitesFilter } from '@/components/PageSitesFilter/PageSitesFilter'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { PageTextSearchFilter } from '@/components/PageSearchFilter/PageTextSearchFilter.tsx'
import { UserRole } from '@/features/user/api'
import { useCompany } from '@/contexts'
import { useFilteredAllowedSites } from '@/hooks/useFilteredAllowedSites'

export const Route = createFileRoute('/_authenticated/$companySlug/route-planning/')({
  beforeLoad: ({ context }) => {
    // Check if routes feature is enabled
    if (!isFeatureEnabled(FEATURES.ROUTES)) {
      throw redirect({
        to: '/not-found'
      })
    }

    context.auth.rejectIfMissingPermissions(['ROUTING_READ'])
  },
  component: AllRoutes,
  head: () => ({
    // use the i18n instance to translate without hooks
    meta: [{ title: i18n.t('_routes:routes.route-planning.main.maintitle') }]
  })
})

// @ts-expect-error Keeping this here for when we implement real durations
function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  return `${hours}:${minutes.toString().padStart(2, '0')}`
}

const mockVehicle = {
  id: '00000000-0000-0000-0000-000000000001',
  siteId: '1',
  name: 'C001',
  vehicleType: 'VAN',
  model: 'Flex Step-in Van',
  vin: 'asdasdasdasdasd',
  vehicleHealth: 'HIGH',
  vehicleStatus: 'AVAILABLE',
  engineType: 'BEV',
  isKeyless: false,
  lastPosition: undefined,
  integrationSource: {
    type: 'GEOTAB',
    identifier: 'asdasdasdasdasd'
  },
  acquisitionOdometer: {
    date: new Date(),
    value: 0
  }
} satisfies Vehicle

function useVehicle(siteIds?: string[], routeIndex?: number) {
  const vehicleListFromDatabase = useAllVehiclesForCompany(
    siteIds ? { siteIds } : undefined
  )

  return useMemo(() => {
    if (routeIndex === undefined) {
      return mockVehicle
    }

    const vehicles = vehicleListFromDatabase.data?.pages.flatMap(p => p.content).toReversed()

    const vehicle = vehicles?.at(routeIndex) ?? mockVehicle

    return {
      ...vehicle,
      model: vehicle.model
        .split(' ')
        .map(it => it.at(0)?.toUpperCase() + it.slice(1))
        .join(' ')
    }
  }, [vehicleListFromDatabase, routeIndex])
}

const mockDriver = {
  id: 'foo',
  firstName: 'Jhon',
  lastName: 'Doe',
  email: 'foobar@example.com',
  phoneNumber: '+31 12345678',
  isActivated: true,
  role: 'DRIVER',
  login: { type: 'PHONE', identifier: '+31 12345678' },
  language: 'en'
} satisfies User

function useDriver(siteIds?: string[], routeIndex?: number) {
  const driverListFromDatabase = useGetAllUsersByRole(UserRole.enum.DRIVER, siteIds)

  return useMemo(() => {
    if (routeIndex === undefined) {
      return mockDriver
    }

    return driverListFromDatabase?.data?.at(routeIndex) ?? mockDriver
  }, [driverListFromDatabase, routeIndex])
}

function AllRoutes() {
  const { sites } = useCompany()
  const navigate = Route.useNavigate()
  const { t, i18n } = useTranslation('_routes')
  const { sites: desiredSiteIds } = Route.useSearch()
  const [searchQuery, setSearchQuery] = useState('')

  const { filteredSites } = useFilteredAllowedSites(sites, desiredSiteIds)

  const itineraryList = useRouteList()
  const [selectedItinerary, setSelectedItinerary] = useState<DrivingRoute>()

  const selectedDriver = useDriver(
    filteredSites.map(site => site.id),
    itineraryList?.data?.findIndex(route => route.id === selectedItinerary?.id)
  )
  const selectedVehicle = useVehicle(
    filteredSites.map(site => site.id),
    itineraryList?.data?.findIndex(route => route.id === selectedItinerary?.id)
  )

  useEffect(() => {
    if (
      selectedItinerary === undefined &&
      itineraryList.data !== undefined &&
      itineraryList.data.length > 0
    ) {
      setSelectedItinerary(itineraryList.data[0])
    }
  }, [itineraryList, selectedItinerary])

  const [selectedDate, setSelectedDate] = useState<Date | null>(startOfDay(new Date()))

  const handleManageRoutesClick = () => {
    navigate({
      to: '/$companySlug/route-planning/assignment'
    })
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4
        }}
      >
        <PageHeader>{t('routes.route-planning.main.maintitle')}</PageHeader>

        <Button variant="contained" size="medium" onClick={handleManageRoutesClick}>
          <ShuffleIcon sx={{ mr: 1 }} />
          <Typography variant="buttonText">
            {t('routes.route-planning.main.manageRoutesButton')}
          </Typography>
        </Button>
      </Box>
      {/* Filter Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Search Bar */}
          <PageTextSearchFilter
            placeholder={t('routes.route-planning.main.searchPlaceholder')}
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 4
          }}
        >
          <DatePicker
            value={selectedDate}
            onChange={setSelectedDate}
            slotProps={{
              textField: {
                size: 'small'
              }
            }}
          />
          <PageSitesFilter />
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid size={4}>
          {itineraryList.data?.map(itinerary => (
            <Card
              onClick={() => setSelectedItinerary(itinerary)}
              sx={theme => ({
                borderLeft:
                  selectedItinerary?.id === itinerary.id
                    ? `3px solid ${blueTechVariants[600]} !important`
                    : `3px solid transparent`,
                ['&:hover']: {
                  cursor: 'pointer',
                  borderLeft: `3px solid ${theme.palette.grey[600]}`
                },
                marginBottom: 3
              })}
            >
              <Stack direction="column" gap={2}>
                <Stack direction="row">
                  <ContainedIcon
                    variant="info"
                    icon={RouteIcon}
                    size={40}
                    sx={{ mr: 2 }}
                  />
                  <Stack direction="column">
                    <Typography variant="subtitle6" lineHeight={1.2} color="secondary">
                      {itinerary?.id ?? 'Loading...'}
                    </Typography>
                    <Typography variant="body4" color="grey.A700">
                      {i18n?.language === 'fr'
                        ? itinerary.description.fr + ' - ' + itinerary.sector.fr
                        : itinerary.description.en + ' - ' + itinerary.sector.en}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={theme => ({
                    pr: 2,
                    fontSize: theme.typography.pxToRem(12)
                  })}
                >
                  <PropertyBlock
                    icon={<NearMeOutlinedIcon />}
                    value={itinerary?.totalDistanceKm ?? 'N/A'}
                    label={t('routes.route-planning.main.listRoutes.totalDistanceLabel')}
                    unit="km"
                  />

                  <PropertyBlock
                    icon={<UnarchiveOutlinedIcon />}
                    value={itinerary?.numberOfPackages ?? 'N/A'}
                    label={t(
                      'routes.route-planning.main.listRoutes.numberOfPackagesLabel'
                    )}
                  />

                  <PropertyBlock
                    icon={<AccessTimeOutlinedIcon />}
                    value={itinerary?.estimatedDeliveryTime ?? 'N/A'}
                    label={t(
                      'routes.route-planning.main.listRoutes.estimatedDeliveryTimeLabel'
                    )}
                  />

                  <PropertyBlock
                    icon={<AirlineStopsOutlinedIcon />}
                    value={itinerary?.numberOfDestinations ?? 'N/A'}
                    label={t(
                      'routes.route-planning.main.listRoutes.numberOfDestinationsLabel'
                    )}
                  />
                </Stack>
              </Stack>
            </Card>
          ))}
        </Grid>
        <Grid size={8} sx={{ minHeight: '70vh' }}>
          {selectedItinerary && (
            <RouteMap
              locations={selectedItinerary.locations}
              viewPort={{ padding: { top: 50, bottom: 250, left: 50, right: 50 } }}
            >
              <Card
                variant="glass"
                sx={{
                  position: 'absolute',
                  zIndex: 1,
                  bottom: 60,
                  left: 0,
                  right: 0,
                  margin: '0 auto',
                  width: 'fit-content',
                  py: 2,
                  px: 2.5,
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <DriverInfoSection
                    name={`${selectedDriver.firstName} ${selectedDriver.lastName}`}
                    phone={selectedDriver.login.identifier}
                    contract="CDI"
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <VehicleInfoSection
                    name={selectedVehicle.name}
                    model={selectedVehicle.model}
                    engineType="Electrique"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Bottom Section: Route Metrics */}
                <Grid container spacing={10} rowSpacing={2}>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <MetricItem
                      icon={
                        <ContainedIcon
                          variant="info"
                          icon={NearMeOutlinedIcon}
                          size={24}
                          sx={{ color: 'text.secondary', padding: '0.2em' }}
                        />
                      }
                      value={
                        selectedItinerary.totalDistanceKm
                          ? t('routes.route-planning.main.metrics.kms', {
                              kms: selectedItinerary.totalDistanceKm
                            })
                          : '…'
                      }
                      label={t('routes.route-planning.main.summary.totalDistance')}
                    />
                  </Grid>

                  <Grid size={{ xs: 6, md: 3 }}>
                    <MetricItem
                      icon={
                        <ContainedIcon
                          variant="info"
                          icon={InboxIcon}
                          size={24}
                          sx={{ color: 'text.secondary', padding: '0.2em' }}
                        />
                      }
                      value={selectedItinerary.numberOfPackages}
                      label={t('routes.route-planning.main.summary.packages')}
                    />
                  </Grid>

                  <Grid size={{ xs: 6, md: 3 }}>
                    <MetricItem
                      icon={
                        <ContainedIcon
                          variant="info"
                          icon={AccessTimeIcon}
                          size={24}
                          sx={{ color: 'text.secondary', padding: '0.2em' }}
                        />
                      }
                      value={
                        selectedItinerary.estimatedDeliveryTime
                          ? selectedItinerary.estimatedDeliveryTime
                          : '...'
                      }
                      label={t('routes.route-planning.main.summary.totalDuration')}
                    />
                  </Grid>

                  <Grid size={{ xs: 6, md: 3 }}>
                    <MetricItem
                      icon={
                        <ContainedIcon
                          variant="info"
                          icon={AirlineStopsOutlinedIcon}
                          size={24}
                          sx={{ color: 'text.secondary', padding: '0.2em' }}
                        />
                      }
                      value={selectedItinerary.numberOfDestinations}
                      label={t('routes.route-planning.main.summary.stops')}
                    />
                  </Grid>
                </Grid>
              </Card>
            </RouteMap>
          )}
        </Grid>
      </Grid>
    </>
  )
}
